// WHAT: Custom hook for filtering roadmap items based on context filter state
// WHY:  Provides memoized filtered roadmap items to avoid unnecessary recalculations
// DOMAIN: Admin → Roadmap
// DEPENDENCIES: useRoadmapContext hook, Roadmap_Item type
// RISKS:
//   - NOTE: Module filter uses includes() - partial matches may be unexpected
//   - NOTE: Search filter is case-insensitive but may match unintended content

import { useMemo } from "react";

import { useRoadmapContext } from "../context/useRoadmapContext";
import { type Roadmap_Item } from "../types/roadmap-types";

/**
 * WHAT:
 *   Custom hook that returns filtered roadmap items based on context filter state.
 *
 * WHY:
 *   Encapsulates filtering logic and provides memoized results to prevent
 *   unnecessary recalculations on every render.
 *
 * RETURNS:
 *   Roadmap_Item[] — Filtered array of roadmap items
 *
 * @returns {Roadmap_Item[]} Gefiltertes Array von Roadmap-Items
 *
 * BEHAVIOR:
 *   - Filters items based on type, status, module, search, and showCompleted
 *   - Hides completed items if showCompleted is false
 *   - Uses case-insensitive matching for module and search filters
 *   - Memoizes result based on items and filter dependencies
 *
 * RISKS:
 *   - NOTE: Module filter uses includes() - "core" matches "score" and "core"
 *   - NOTE: Search matches title and description - may return unexpected results
 *   - NOTE: All filters must match (AND logic) - no OR options available
 *
 */
export const useFilteredRoadmap = (): Roadmap_Item[] => {
  // NOTE: Get items and filter from context
  const { filter, items } = useRoadmapContext();

  // NOTE: Memoize filtered results to avoid recalculation on every render
  return useMemo(() => {
    return items.filter((item) => {
      // NOTE: Show/Hide completed items filter - hide 'done' items by default
      const showCompleted = filter.showCompleted ?? false;
      if (!showCompleted && item.status === "done") {
        return false;
      }

      // NOTE: Existing filters - all must match (AND logic)
      const matchesType = !filter.type || item.type === filter.type;
      const matchesStatus = !filter.status || item.status === filter.status;
      // NOTE: Module filter uses includes() for partial matching (case-insensitive)
      const matchesModule =
        !filter.module || item.module.toLowerCase().includes(filter.module.toLowerCase());
      // NOTE: Search filter matches title or description (case-insensitive)
      const matchesSearch =
        !filter.search ||
        item.title.toLowerCase().includes(filter.search.toLowerCase()) ||
        item.description.toLowerCase().includes(filter.search.toLowerCase());

      return matchesType && matchesStatus && matchesModule && matchesSearch;
    });
  }, [items, filter]);
};
